import { useOrgContext } from '@/org/contexts/OrgContext'
import { Log_Bool_Exp, useGovernanceLogsQuery } from '@gql'
import { useEffect, useMemo, useRef } from 'react'

// ACTU-01 (#48): the governance tab of the news page — every change to the org
// chart, newest first, loaded page by page as the reader scrolls.
//
// With a circle picked, the feed keeps the changes of that circle AND of all
// it contains (Yohan, 21/09/2026): the circle and its descendants, each by its
// circle id (circle, member and link changes) and its role id (role changes).
// A leaf role is a circle without descendants, so the same rule gives its own
// changes only.
export function useGovernanceFeed(
  circleId: string | undefined,
  skip: boolean,
  limit = 20
) {
  const { orgId, orgData } = useOrgContext()
  const bottomRef = useRef(null)

  const where = useMemo((): Log_Bool_Exp | undefined => {
    if (!orgId) return undefined
    if (!circleId) return { orgId: { _eq: orgId } }
    const circle = orgData?.getCircle(circleId)
    if (!circle || !orgData) return undefined
    const scope = [circle, ...orgData.descendantsOf(circleId)]
    const entityIds = [...new Set(scope.flatMap((c) => [c.id, c.roleId]))]
    return { orgId: { _eq: orgId }, entityId: { _in: entityIds } }
  }, [orgId, orgData, circleId])

  const { data, error, loading, fetchMore } = useGovernanceLogsQuery({
    skip: skip || !where,
    variables: { where: where!, limit },
    fetchPolicy: 'cache-and-network',
    initialFetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })
  const logs = data?.log
  const count = data?.log_aggregate.aggregate?.count

  // Load more changes when the reader reaches the bottom (as useNewsFeed)
  useEffect(() => {
    if (!bottomRef.current || !logs || logs.length === count || loading) return
    const target = bottomRef.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        fetchMore({
          variables: { offset: logs.length },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) return previousResult
            return {
              ...fetchMoreResult,
              log: [...previousResult.log, ...fetchMoreResult.log],
            }
          },
        })
      },
      { root: null, rootMargin: '0px', threshold: 1 }
    )
    observer.observe(target)
    return () => observer.unobserve(target)
  }, [logs, loading])

  return { logs, error, loading, bottomRef }
}
